import React, { useEffect, useRef } from "react";
import styled from "@emotion/styled";
import { ChatWidgetDrawerState } from "@cma/store/chat-widget.store";
import {
  HALF_EXPANDED_VH,
  NAVBAR_HEIGHT_REM,
  useSnapDrawer,
} from "@shared/hooks/useSnapDrawer";

const DRAG_HANDLE_HEIGHT_PX = 40; // 2.5rem

const getHeightStyles = (state: ChatWidgetDrawerState): string => {
  switch (state) {
    case "collapsed":
      return `height: var(--collapsed-h, 9rem);`;
    case "half-expanded":
      return `height: ${HALF_EXPANDED_VH}dvh;`;
    case "expanded":
      return `height: calc(100dvh - ${NAVBAR_HEIGHT_REM}rem);`;
  }
};

const Container = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
`;

const MainContent = styled.div<{
  state: ChatWidgetDrawerState;
  hasWidget?: boolean;
}>`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  display: ${(p) => (p.hasWidget ? "block" : "none")};
  padding-bottom: ${(p) => {
    switch (p.state) {
      case "collapsed":
        return "5.75rem";
      case "half-expanded":
        return `${HALF_EXPANDED_VH}dvh`;
      case "expanded":
        return `calc(100dvh - ${NAVBAR_HEIGHT_REM}rem)`;
    }
  }};
`;

const StackedPanel = styled.div<{
  state: ChatWidgetDrawerState;
  hasWidget?: boolean;
}>`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  overflow: hidden;
  background-color: ${(p) => p.theme.colors.background};
  border-top-left-radius: ${(p) => (p.hasWidget ? "1rem" : "0")};
  border-top-right-radius: ${(p) => (p.hasWidget ? "1rem" : "0")};
  /* TODO: Port to UI theme lib */
  box-shadow: ${(p) =>
    p.hasWidget
      ? `0 -4px 40px 4px ${p.theme.name === "light" ? "#c7c3c1" : "#1d1d1d"}`
      : "none"};
  display: flex;
  flex-direction: column;
  transition: height 400ms cubic-bezier(0.32, 0.72, 0, 1);
  touch-action: none;
  z-index: 300;

  ${(p) => (p.hasWidget ? getHeightStyles(p.state) : `height: 100dvh;`)}
`;

const DragHandle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 2.5rem;
  flex-shrink: 0;
  cursor: grab;
  touch-action: none;

  &:active {
    cursor: grabbing;
  }

  &::before {
    content: "";
    width: 4rem;
    height: 0.25rem;
    background-color: ${(p) => p.theme.input.colors.border};
    border-radius: 0.125rem;
  }
`;

const StackedContent = styled.div<{
  hasWidget?: boolean;
}>`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  ${(p) => !p.hasWidget && `height: 100dvh;`}
`;

const DrawerContent = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  isolation: isolate;
`;

const Footer = styled.div`
  position: relative;
  flex-shrink: 0;
  background-color: ${(p) => p.theme.colors.background};
`;

interface StackedScreenProps {
  mainContent: React.ReactNode;
  drawerContent: React.ReactNode;
  footerContent: (isDragging: boolean) => React.ReactNode;
  drawerState: ChatWidgetDrawerState;
  onDrawerStateChange: (state: ChatWidgetDrawerState) => void;
  showDragHandle?: boolean;
  hasWidget?: boolean;
}

export const StackedScreen = (props: StackedScreenProps) => {
  const {
    mainContent,
    drawerContent,
    footerContent,
    drawerState,
    onDrawerStateChange,
    showDragHandle = true,
    hasWidget = true,
  } = props;

  const { panelRef, bind, isDragging } = useSnapDrawer(
    drawerState,
    onDrawerStateChange,
  );
  const footerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const footer = footerRef.current;
    const panel = panelRef.current;
    if (!footer || !panel) return;

    const update = () => {
      panel.style.setProperty(
        "--collapsed-h",
        `${DRAG_HANDLE_HEIGHT_PX + footer.offsetHeight}px`,
      );
    };

    const observer = new ResizeObserver(update);
    observer.observe(footer);
    update();

    return () => observer.disconnect();
  }, [panelRef]);

  return (
    <Container id="stacked-screen-container">
      <MainContent state={drawerState} hasWidget={hasWidget}>
        {mainContent}
      </MainContent>

      <StackedPanel ref={panelRef} state={drawerState} hasWidget={hasWidget}>
        {showDragHandle && (
          <DragHandle
            {...(hasWidget ? bind() : {})}
            role="button"
            tabIndex={0}
          />
        )}
        <StackedContent hasWidget={hasWidget}>
          <DrawerContent>{drawerContent}</DrawerContent>
          <Footer ref={footerRef}>{footerContent(isDragging)}</Footer>
        </StackedContent>
      </StackedPanel>
    </Container>
  );
};
